/**
 * SitesListPage -- Lists all CMS sites with publish status.
 *
 * Uses useSiteStore (ADR-021), List + LoadingState (ADR-022).
 * Clicking a site row navigates to the SiteDetailPage.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteStore, useSiteActions } from '../../state/cms/useSiteStore';
import { List } from '../../components/ui/List';
import { LoadingState } from '../../components/ui/LoadingState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { logger } from '../../services/logger.client';
import type { CmsSite } from '../../types/cms.types';

const log = logger.child({ module: 'SitesListPage' });

/* ------------------------------------------------------------------ */
/*  Small internal components                                         */
/* ------------------------------------------------------------------ */

function DirtyBadge({ isDirty }: { isDirty: boolean }) {
  return (
    <span className={`badge ${isDirty ? 'badge--needs-publish' : 'badge--published'}`}>
      {isDirty ? 'Needs Publish' : 'Published'}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never published';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Create Site Modal                                                 */
/* ------------------------------------------------------------------ */

interface CreateSiteFormProps {
  onSubmit: (data: Partial<CmsSite>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CreateSiteForm({ onSubmit, onCancel, isLoading }: CreateSiteFormProps) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !domain.trim()) return;
    onSubmit({ name: name.trim(), domain: domain.trim() });
  }

  return (
    <div className="cms-modal-overlay" onClick={onCancel}>
      <div className="cms-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="cms-modal__title">Create New Site</h2>
        <form className="cms-modal__form" onSubmit={handleSubmit} noValidate>
          <Input
            label="Site Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Senior Engineering Resume"
            required
          />
          <Input
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="e.g. john-engineer.example.com"
            required
          />
          <div className="cms-modal__actions">
            <Button variant="secondary" size="md" onClick={onCancel} type="button">
              Cancel
            </Button>
            <Button variant="primary" size="md" loading={isLoading} type="submit">
              Create Site
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function SitesListPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const sites = useSiteStore((s) => s.sites);
  const isLoading = useSiteStore((s) => s.isLoading);
  const error = useSiteStore((s) => s.error);
  const { fetchSites, createSite } = useSiteActions();

  useEffect(() => {
    log.info('Sites list page mounted -- fetching sites');
    void fetchSites();
  }, [fetchSites]);

  async function handleCreateSite(data: Partial<CmsSite>) {
    const site = await createSite(data);
    if (site) {
      log.info('Navigating to new site', { id: site.id });
      setShowCreate(false);
      navigate(`/cms/sites/${site.id}`);
    }
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Sites</h1>
        <Button variant="primary" size="md" onClick={() => setShowCreate(true)}>
          Create Site
        </Button>
      </header>

      <LoadingState loading={isLoading} error={error}>
        <List<CmsSite>
          items={sites}
          keyExtractor={(s) => s.id}
          emptyMessage="No sites yet. Click 'Create Site' to get started."
          className="cms-site-list"
        >
          {(site) => (
            <li
              className="cms-site-card"
              onClick={() => navigate(`/cms/sites/${site.id}`)}
            >
              <div className="cms-site-card__header">
                <h3 className="cms-site-card__name">{site.name}</h3>
                <DirtyBadge isDirty={site.is_dirty} />
              </div>
              <p className="cms-site-card__domain">{site.domain}</p>
              <p className="cms-site-card__meta">
                {formatDate(site.last_published_at)}
              </p>
            </li>
          )}
        </List>
      </LoadingState>

      {showCreate && (
        <CreateSiteForm
          onSubmit={handleCreateSite}
          onCancel={() => setShowCreate(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
